import { AnimatePresence, motion } from "framer-motion";
import { MessageSquareText, Send, X } from "lucide-react";
import { useEffect, useState } from "react";

const ResponseModal = ({ isOpen, issue, loading = false, onClose, onSubmit }) => {
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setMessage("");
    }
  }, [isOpen, issue?._id]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!message.trim() || !issue?._id || loading) {
      return;
    }

    onSubmit?.(issue._id, message.trim());
  };

  return (
    <AnimatePresence>
      {isOpen && issue && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/65 px-4 py-6 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full max-w-2xl rounded-[2rem] border border-white/60 bg-white p-6 shadow-2xl shadow-slate-900/30"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                  Candidate Response
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                  Respond to "{issue.title}"
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  Send a visible update to voters and move the issue into active review.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 rounded-[1.5rem] border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-slate-900 p-2 text-white">
                  <MessageSquareText className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {issue.constituency || "Unknown constituency"}, {issue.district || "Unknown district"}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{issue.description}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Public Response
                </label>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={6}
                  placeholder="Share what action you are taking, expected timeline, and what citizens should know next."
                  className="w-full rounded-[1.5rem] border border-slate-200 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-orange-400"
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200/70 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <Send className="h-4 w-4" />
                  {loading ? "Sending..." : "Send Response"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResponseModal;
